/**
 * TPG Player — Capability Module framework (Sprint 8 Phase B1).
 *
 * Features are enabled by capability modules, not by forking the player.
 * Platform / licensee config + Media Asset.capabilities select modules.
 * Modules declare requirements; the shell enables UI/plugins accordingly.
 */

/** Canonical capability module ids (stable for SDK). */
export var CapabilityModuleId = Object.freeze({
  SUBTITLES: "subtitles",
  AUDIO_TRACKS: "audioTracks",
  PLAYBACK_SPEED: "playbackSpeed",
  CHAPTERS: "chapters",
  BOOKMARKS: "bookmarks",
  RESUME: "resume",
  THEATER: "theater",
  FULLSCREEN: "fullscreen",
  BRANDED_LOADING: "brandedLoading",
  WATERMARK: "watermark",
  CHROMECAST: "chromecast",
  AIRPLAY: "airplay",
  ACCESSIBILITY: "accessibility",
  ANALYTICS: "analytics",
  DRM: "drm",
  AVOD: "avod",
  COMPANION: "companion",
  LIVE: "live",
  DVR: "dvr",
  PLAYLIST: "playlist",
  CHANNEL: "channel",
  PLUGINS: "plugins",
  EVENT_COUNTDOWN: "eventCountdown",
  INTERACTIVE: "interactive",
});

/**
 * @typedef {object} CapabilityModule
 * @property {string} id
 * @property {string} name
 * @property {string} owner  "player" | "platform" — who owns business rules
 * @property {string} [description]
 * @property {boolean} [defaultEnabled]
 * @property {string[]} [requires]  other module ids
 * @property {string[]} [presentationStrategies]  empty = all
 */

/** Registry of built-in capability modules. */
export var CAPABILITY_MODULES = Object.freeze({
  subtitles: {
    id: CapabilityModuleId.SUBTITLES,
    name: "Subtitles",
    owner: "player",
    defaultEnabled: true,
    presentationStrategies: ["video", "live", "embed"],
  },
  audioTracks: {
    id: CapabilityModuleId.AUDIO_TRACKS,
    name: "Multiple audio tracks",
    owner: "player",
    defaultEnabled: true,
    presentationStrategies: ["video", "live"],
  },
  playbackSpeed: {
    id: CapabilityModuleId.PLAYBACK_SPEED,
    name: "Playback speed",
    owner: "player",
    defaultEnabled: true,
  },
  chapters: {
    id: CapabilityModuleId.CHAPTERS,
    name: "Chapter markers",
    owner: "player",
    defaultEnabled: false,
  },
  bookmarks: {
    id: CapabilityModuleId.BOOKMARKS,
    name: "Bookmarks",
    owner: "player",
    defaultEnabled: false,
  },
  resume: {
    id: CapabilityModuleId.RESUME,
    name: "Resume playback",
    owner: "player",
    defaultEnabled: true,
  },
  theater: {
    id: CapabilityModuleId.THEATER,
    name: "Theater mode",
    owner: "player",
    defaultEnabled: true,
    presentationStrategies: ["video", "live", "companion", "embed"],
  },
  fullscreen: {
    id: CapabilityModuleId.FULLSCREEN,
    name: "Fullscreen",
    owner: "player",
    defaultEnabled: true,
  },
  brandedLoading: {
    id: CapabilityModuleId.BRANDED_LOADING,
    name: "Branded loading",
    owner: "player",
    defaultEnabled: true,
  },
  watermark: {
    id: CapabilityModuleId.WATERMARK,
    name: "Watermarking",
    owner: "player",
    defaultEnabled: false,
  },
  chromecast: {
    id: CapabilityModuleId.CHROMECAST,
    name: "Chromecast",
    owner: "player",
    defaultEnabled: false,
  },
  airplay: {
    id: CapabilityModuleId.AIRPLAY,
    name: "AirPlay",
    owner: "player",
    defaultEnabled: false,
  },
  accessibility: {
    id: CapabilityModuleId.ACCESSIBILITY,
    name: "Accessibility",
    owner: "player",
    defaultEnabled: true,
  },
  analytics: {
    id: CapabilityModuleId.ANALYTICS,
    name: "Analytics hooks",
    owner: "player",
    defaultEnabled: true,
  },
  drm: {
    id: CapabilityModuleId.DRM,
    name: "DRM",
    owner: "player",
    defaultEnabled: false,
    description: "License acquisition is platform-supplied; EME is player",
  },
  avod: {
    id: CapabilityModuleId.AVOD,
    name: "AVOD",
    owner: "platform",
    defaultEnabled: false,
    description: "Ad cues from platform; player runs ad presentation plugin",
  },
  companion: {
    id: CapabilityModuleId.COMPANION,
    name: "Companion presentation",
    owner: "player",
    defaultEnabled: false,
    presentationStrategies: ["companion", "audio", "playlist"],
  },
  live: {
    id: CapabilityModuleId.LIVE,
    name: "Live streaming",
    owner: "player",
    defaultEnabled: false,
  },
  dvr: {
    id: CapabilityModuleId.DVR,
    name: "DVR",
    owner: "player",
    defaultEnabled: false,
    requires: ["live"],
  },
  playlist: {
    id: CapabilityModuleId.PLAYLIST,
    name: "Playlist presentation",
    owner: "player",
    defaultEnabled: false,
  },
  channel: {
    id: CapabilityModuleId.CHANNEL,
    name: "Channel presentation",
    owner: "player",
    defaultEnabled: false,
    requires: ["live"],
  },
  plugins: {
    id: CapabilityModuleId.PLUGINS,
    name: "Plugin architecture",
    owner: "player",
    defaultEnabled: true,
  },
  eventCountdown: {
    id: CapabilityModuleId.EVENT_COUNTDOWN,
    name: "Event countdown",
    owner: "player",
    defaultEnabled: false,
  },
  interactive: {
    id: CapabilityModuleId.INTERACTIVE,
    name: "Interactive overlays",
    owner: "player",
    defaultEnabled: false,
  },
});

/**
 * Resolve enabled capability modules from flags + strategy + asset.
 *
 * @param {object} [options]
 * @param {object} [options.featureFlags]  resolveFeatureFlags() shape
 * @param {object} [options.strategy]  PresentationStrategy
 * @param {object} [options.asset]  MediaAsset
 * @param {string[]} [options.licenseeAllowlist]  commercial capability allowlist
 * @returns {string[]} enabled module ids
 */
export function resolveCapabilityModules(options) {
  options = options || {};
  var flags = options.featureFlags || {};
  var strategy = options.strategy || null;
  var asset = options.asset || null;
  var allow = options.licenseeAllowlist || null;

  var enabled = {};
  var id;
  for (id in CAPABILITY_MODULES) {
    if (!Object.prototype.hasOwnProperty.call(CAPABILITY_MODULES, id)) continue;
    var mod = CAPABILITY_MODULES[id];
    if (mod.defaultEnabled) enabled[id] = true;
  }

  // Feature flags (host / white-label)
  if (flags.subtitles === false) delete enabled.subtitles;
  if (flags.audioTracks === false) delete enabled.audioTracks;
  if (flags.playbackSpeed === false) delete enabled.playbackSpeed;
  if (flags.chapters) enabled.chapters = true;
  if (flags.continueWatching || flags.resume !== false) enabled.resume = true;
  if (flags.theaterMode === false) delete enabled.theater;
  if (flags.fullscreen === false) delete enabled.fullscreen;
  if (flags.brandedLoading) enabled.brandedLoading = true;
  if (flags.watermarks) enabled.watermark = true;
  if (flags.casting || flags.chromecast) enabled.chromecast = true;
  if (flags.casting || flags.airplay) enabled.airplay = true;
  if (flags.highContrast || flags.accessibility !== false) {
    enabled.accessibility = true;
  }
  if (flags.analytics) enabled.analytics = true;
  if (flags.drm) enabled.drm = true;
  if (flags.avod || flags.ads) enabled.avod = true;
  if (flags.companionPresentation) enabled.companion = true;
  if (flags.live) enabled.live = true;
  if (flags.dvr) enabled.dvr = true;
  if (flags.playlist) enabled.playlist = true;
  if (flags.channel) enabled.channel = true;

  // Strategy defaults
  if (strategy && Array.isArray(strategy.defaultCapabilities)) {
    strategy.defaultCapabilities.forEach(function (cid) {
      var mapped = cid;
      if (cid === "playbackSpeed") mapped = "playbackSpeed";
      if (cid === "audioTracks") mapped = "audioTracks";
      if (cid === "eventCountdown") mapped = "eventCountdown";
      if (CAPABILITY_MODULES[mapped]) enabled[mapped] = true;
    });
  }

  // Asset-requested capabilities
  if (asset && Array.isArray(asset.capabilities)) {
    asset.capabilities.forEach(function (cid) {
      if (CAPABILITY_MODULES[cid]) enabled[cid] = true;
    });
  }

  // Strategy allowlist filter
  if (strategy) {
    Object.keys(CAPABILITY_MODULES).forEach(function (cid) {
      var mod = CAPABILITY_MODULES[cid];
      var allowed = mod.presentationStrategies;
      if (
        allowed &&
        allowed.length &&
        allowed.indexOf(strategy.id) === -1 &&
        enabled[cid] &&
        !mod.defaultEnabled
      ) {
        // keep defaults; only strip strategy-incompatible optional modules
        if (
          cid === "companion" ||
          cid === "live" ||
          cid === "dvr" ||
          cid === "channel" ||
          cid === "playlist"
        ) {
          if (allowed.indexOf(strategy.id) === -1) delete enabled[cid];
        }
      }
    });
  }

  // Commercial allowlist
  if (allow && allow.length) {
    Object.keys(enabled).forEach(function (cid) {
      if (allow.indexOf(cid) === -1) delete enabled[cid];
    });
  }

  // Dependency resolution
  Object.keys(enabled).forEach(function (cid) {
    var mod = CAPABILITY_MODULES[cid];
    if (mod && mod.requires) {
      mod.requires.forEach(function (req) {
        if (!enabled[req]) delete enabled[cid];
      });
    }
  });

  return Object.keys(enabled);
}

/**
 * @param {string[]} enabledIds
 * @param {string} moduleId
 * @returns {boolean}
 */
export function hasCapabilityModule(enabledIds, moduleId) {
  return Array.isArray(enabledIds) && enabledIds.indexOf(moduleId) !== -1;
}

/**
 * Ownership boundary helper for docs / SDK.
 * @returns {{ player: string[], platform: string[] }}
 */
export function capabilityOwnershipMap() {
  var player = [];
  var platform = [];
  Object.keys(CAPABILITY_MODULES).forEach(function (id) {
    var mod = CAPABILITY_MODULES[id];
    if (mod.owner === "platform") platform.push(id);
    else player.push(id);
  });
  return { player: player, platform: platform };
}

export default {
  CapabilityModuleId,
  CAPABILITY_MODULES,
  resolveCapabilityModules,
  hasCapabilityModule,
  capabilityOwnershipMap,
};
